import { CircleChevronLeft, SquarePen } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

// Define types for our options
interface BaseOption {
  id: string;
  name: string;
}

interface WholesalerOption extends BaseOption {
  wholeSalerName: string;
}

interface ManufacturerOption extends BaseOption {
  manufacturerName: string;
}

interface MarketerOption extends BaseOption {
  marketerName: string;
}

type OptionType = BaseOption | WholesalerOption | ManufacturerOption;
import toast from "react-hot-toast";
import { InverntroyService } from "../services/api/inventroy.service";
import { ClinicService } from "../services/api/clinic.service";
import { SearchableDropdown } from "./SearchableDropdown";

interface Product {
  id: string;
  productId: string;
  productName: string;
  manufacturerId: string;
  marketerId: string;
  expiryDate: string;
  batchNumber: string;
  hsnNumber: string;
  tabletsPerStripes: string;
  noOfItems: number;
  mrp: number;
  freeItems: number;
  productRate: number;
  discount: number;
  cgst: number;
  sgst: number;
  newCgst: number;
  newSgst: number;
  newMrp: number;
  landingCost: number;
  categoryId: number;
}

type ChargeHeadAction = "increase" | "discount";

// Master charge head (freight, packing, cash discount, ...) coming from the backend
interface InvoiceChargesHead {
  id: number;
  headType: string;
  headHsnNumber: string | null;
  headGst: number | null;
  action: ChargeHeadAction;
  createdAt?: string;
}

// A head applied to this invoice. `amount` is what the admin typed, before head GST.
interface InvoiceCharge {
  headId: number;
  amount: number;
}

const formStyles = {
  lableStyle: "block text-sm text-[#3F5260] font-medium mb-1",
  inputStyle:
    "w-full px-3 py-2 border border-[#D0D5DE] outline-none rounded-lg focus:ring-1 focus:ring-emerald-500 text-sm",
};

const InwardNewstockModal = ({ onClose }: { onClose: () => void }) => {
  const [clinicOptions, setClinicOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [clinicPageInfo, setClinicPageInfo] = useState({
    currentPage: 1,
    hasMore: true,
    isLoading: false,
  });
  const [productOptions, setProductOptions] = useState<OptionType[]>([]);
  const [productSearchText, setProductSearchText] = useState<string>("");
  const [showProductSuggestions, setShowProductSuggestions] =
    useState<boolean>(false);
  const [productPageInfo, setProductPageInfo] = useState({
    currentPage: 1,
    hasMore: true,
  });
  const [wholeSalerOptions, setWholeSalerOptions] = useState<
    WholesalerOption[]
  >([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<
    ManufacturerOption[]
  >([]);
  const [marketerOptions, setMarketerOptions] = useState<MarketerOption[]>([]);
  const [manufacturerSearchText, setManufacturerSearchText] =
    useState<string>("");
  const [marketerSearchText, setMarketerSearchText] = useState<string>("");
  const [showManufacturerSuggestions, setShowManufacturerSuggestions] =
    useState(false);
  const [showMarketerSuggestions, setShowMarketerSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [masterDiscountAmount, setMasterDiscountAmount] = useState(-1);
  const [masterDiscountAmountTemp, setMasterDiscountAmountTemp] = useState();
  const [showAddDiscountButton, setShowAddDiscountButton] = useState(true);
  const [initialGrossTotal, setInitialGrossTotal] = useState(0);
  const [initialGst, setInitialGst] = useState(0);
  // Form state management
  const [isInvoiceValid, setIsInvoiceValid] = useState<boolean | null>(null);
  const [isCheckingInvoice, setIsCheckingInvoice] = useState(false);
  // Search states for wholesaler and clinic
  const [wholesalerSearchText, setWholesalerSearchText] = useState("");
  const [showWholesalerSuggestions, setShowWholesalerSuggestions] =
    useState(false);
  const [clinicSearchText, setClinicSearchText] = useState("");
  const [showClinicSuggestions, setShowClinicSuggestions] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [roundOff, setRoundOff] = useState(0);
  const [roundOffAmount, setRoundOffAmount] = useState(0);
  const [bonusQuantity, setBonusQuantity] = useState(0);
  const [bonus, setBonus] = useState(0);
  const [showBonus, setShowBonus] = useState(false);
  const [useOldGst, setUseOldGst] = useState(false);
  const [chargesHeads, setChargesHeads] = useState<InvoiceChargesHead[]>([]);
  const [invoiceCharges, setInvoiceCharges] = useState<InvoiceCharge[]>([]);
  const [isExtraChargeModalOpen, setIsExtraChargeModalOpen] = useState(false);
  const [selectedHeadId, setSelectedHeadId] = useState("");
  const [extraChargeAmount, setExtraChargeAmount] = useState("");
  const [isNewHeadFormOpen, setIsNewHeadFormOpen] = useState(false);
  const [isSavingHead, setIsSavingHead] = useState(false);
  const [newHeadForm, setNewHeadForm] = useState({
    headType: "",
    headHsnNumber: "",
    headGst: "",
    action: "increase" as ChargeHeadAction,
  });

  const [invoiceData, setInvoiceData] = useState({
    wholesalerId: "",
    clinicId: "",
    invoiceNumber: "",
    invoiceDate: "",
    invoiceAmount: "",
    payment_status: "completed",
  });

  // Product-specific form data
  const [productFormData, setProductFormData] = useState({
    productId: "",
    productName: "",
    manufacturerId: "",
    expiryDate: "",
    batchNumber: "",
    hsnNumber: "",
    marketerId: "",
    tabletsPerStripes: "",
    noOfItems: "",
    mrp: "",
    freeItems: "",
    productRate: "",
    discount: "",
    cgst: "",
    sgst: "",
    newCgst: "",
    newSgst: "",
    newMrp: '',
    categoryId: ''
  });

  useEffect(() => {
    const calculateMrp = () => {
      const baseMrp = Number(productFormData.mrp);
      const dividingGst =
        (Number(productFormData.sgst) + Number(productFormData.cgst)) / 100;
      const multiplyingGst =
        (Number(productFormData.newSgst) + Number(productFormData.newSgst)) / 100;
      const mrpWithoutGst = baseMrp / (1 + dividingGst);
      const updatedMrp = mrpWithoutGst * (1 + multiplyingGst);
      setProductFormData({ ...productFormData, newMrp: updatedMrp.toFixed(2) });
    };
    calculateMrp();
  }, [
    productFormData.mrp,
    useOldGst,
    productFormData.cgst,
    productFormData.sgst,
  ]);

  // Refs for dropdown containers
  const productDropdownRef = useRef<HTMLDivElement>(null);
  const wholesalerDropdownRef = useRef<HTMLDivElement>(null);
  const clinicDropdownRef = useRef<HTMLDivElement>(null);
  const manufacturerDropdownRef = useRef<HTMLDivElement>(null);
  const marketerDropdownRef = useRef<HTMLDivElement>(null);

  // Calculate the gross total without any discount
  const getGrossAmountPerProduct = (
    rate: number,
    discount: number,
    noOfItems: number
  ) => {
    const amount = noOfItems * rate;
    return amount;
  };
  const getOverAllGst = (
    rate: number,
    cgst: number,
    sgst: number,
    discount: number,
    noOfItems: number,
    newCgst: number,
    newSgst: number
  ) => {
    const discountedTotal = noOfItems * rate * (1 - discount / 100);
    if (useOldGst) {
      const gstInculded = discountedTotal * ((cgst + sgst) / 100);
      return gstInculded;
    }
    const gstInculded = discountedTotal * ((newCgst + newSgst) / 100);

    return gstInculded;
  };
  const calculateGrossTotal = useCallback(() => {
    return products.reduce((total, product) => {
      const landingCost = getGrossAmountPerProduct(
        product.productRate,
        product.discount,
        product.noOfItems
      );
      return total + landingCost;
    }, 0);
  }, [products]);

  const calculateDiscountTotal = useCallback(() => {
    const amount = products.reduce((total, product) => {
      const landingCost =
        (product.noOfItems * product.productRate * product.discount) / 100;
      return total + landingCost;
    }, 0);

    return amount.toFixed(2);
  }, [products]);

  const calculateOverAllGst = useCallback(() => {
    return products.reduce((total, product) => {
      const landingCost = getOverAllGst(
        product.productRate,
        product.cgst,
        product.sgst,
        product.discount,
        product.noOfItems,
        product.newCgst,
        product.newSgst
      );
      return total + landingCost;
    }, 0);
  }, [products]);

  const getChargeHead = (headId: number) =>
    chargesHeads.find((head) => head.id === headId);

  // Amount typed by the admin plus the GST of its head
  const getChargeNetAmount = (charge: InvoiceCharge) => {
    const headGst = getChargeHead(charge.headId)?.headGst ?? 0;
    return charge.amount * (1 + headGst / 100);
  };

  // Discount heads reduce the payable amount, increase heads add to it
  const getSignedChargeAmount = (charge: InvoiceCharge) => {
    const netAmount = getChargeNetAmount(charge);
    return getChargeHead(charge.headId)?.action === "discount"
      ? -netAmount
      : netAmount;
  };

  // Invoice charges are split across products in proportion to their
  // quantity (e.g. 10 / (10 + 5 + 4) for the first product)
  const netExtraCharge = invoiceCharges.reduce(
    (sum, charge) => sum + getSignedChargeAmount(charge),
    0
  );
  const totalQuantity = products.reduce(
    (sum, product) => sum + product.noOfItems,
    0
  );

  const allocateByQuantity = (amount: number, product: Product) =>
    totalQuantity > 0 ? (amount * product.noOfItems) / totalQuantity : 0;

  // Per unit share of the charges, which is what the backend stores as extraHead
  const getExtraHeadPerUnit = (product: Product) => {
    const totalItems = product.noOfItems + product.freeItems;
    if (totalItems <= 0) return 0;
    return allocateByQuantity(netExtraCharge, product) / totalItems;
  };

  const getFinalLandingCost = (product: Product) =>
    Math.max(0, product.landingCost + getExtraHeadPerUnit(product));

  // extraHead is always sent as a positive amount, the sign lives in extraHeadAction
  const getExtraHeadPayload = (product: Product) => {
    const extraHead = Number(Math.abs(getExtraHeadPerUnit(product)).toFixed(2));
    if (extraHead <= 0) return {};
    return {
      extraHead,
      extraHeadAction:
        getExtraHeadPerUnit(product) >= 0 ? "increase" : "discount",
    };
  };

  // Recalculate initial gross total whenever products change
  useEffect(() => {
    const newGrossTotal = calculateGrossTotal();
    setInitialGrossTotal(newGrossTotal);

    const discount = calculateDiscountTotal();

    const newGst = calculateOverAllGst();
    setInitialGst(newGst);

    const totalAmount = newGrossTotal + newGst - discount + netExtraCharge;
    const decimalPart = totalAmount - Math.floor(totalAmount);
    let roundedAmount;

    if (decimalPart >= 0.5) {
      roundedAmount = Math.ceil(totalAmount);
    } else {
      roundedAmount = Math.floor(totalAmount);
    }

    const roundOffValue = parseFloat((roundedAmount - totalAmount).toFixed(2));

    setRoundOffAmount(roundedAmount);
    setRoundOff(roundOffValue);
    // Reapply discount whenever products change
    if (masterDiscountAmount >= 0 && masterDiscountAmountTemp !== undefined) {
      const updatedProducts = applyDiscountToProducts(
        products,
        masterDiscountAmount
      );
      setProducts(updatedProducts);
    }
  }, [
    products,
    masterDiscountAmount,
    netExtraCharge,
    calculateGrossTotal,
    calculateOverAllGst,
  ]);

  // Invoice check implementation
  const checkInvoiceNumber = useCallback(async (invoiceNumber: string) => {
    if (!invoiceNumber.trim()) {
      setIsInvoiceValid(null);
      return;
    }

    setIsCheckingInvoice(true);
    try {
      const response = await InverntroyService.CHECKINVOICENUMBERAVAILABILITY(
        invoiceNumber
      );
      const isValid = response.success ?? response.isAvailable;
      setIsInvoiceValid(isValid);
      if (!isValid) {
        toast.error(
          response.message || "This invoice number is already in use"
        );
      }
      return isValid;
    } catch (error) {
      console.error("Error checking invoice number:", error);
      toast.error("Failed to validate invoice number");
      setIsInvoiceValid(null);
      return false;
    } finally {
      setIsCheckingInvoice(false);
    }
  }, []);

  // Handle invoice input change with debounce
  const handleInvoiceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;

      setInvoiceData((prev) => ({
        ...prev,
        [name]: name === "invoiceNumber" ? validateInvoiceNumber(value) : value,
      }));
    },
    []
  );

  const handleInvoiceBlur = useCallback(async () => {
    if (!invoiceData.invoiceNumber.trim()) {
      setIsInvoiceValid(null);
      return;
    }

    setIsCheckingInvoice(true);
    try {
      const response = await InverntroyService.CHECKINVOICENUMBERAVAILABILITY(
        invoiceData.invoiceNumber
      );
      const isValid = response.success ?? response.isAvailable;
      setIsInvoiceValid(isValid);
      if (!isValid) {
        toast.error(
          response.message || "This invoice number is already in use"
        );
      }
    } catch (error) {
      console.error("Error checking invoice number:", error);
      toast.error("Failed to validate invoice number");
      setIsInvoiceValid(null);
    } finally {
      setIsCheckingInvoice(false);
    }
  }, [invoiceData.invoiceNumber]);

  const validateInvoiceNumber = (value: string): string => {
    return value;
  };

  const loadWholeSalerOptions = useCallback(async () => {
    try {
      const response = await InverntroyService.getWholeSalerOptions();
      if (Array.isArray(response)) {
        setWholeSalerOptions(response);
      }
    } catch (error) {
      console.error("Error loading wholesalers:", error);
      toast.error("Failed to load wholesalers");
    }
  }, []);

  const loadManufacturerOptions = useCallback(async () => {
    try {
      const response = await InverntroyService.getManufacturerOptions();
      if (Array.isArray(response)) {
        setManufacturerOptions(response);
      }
    } catch (error) {
      console.error("Error loading manufacturers:", error);
      toast.error("Failed to load manufacturers");
    }
  }, []);

  const loadMarketerOptions = useCallback(async () => {
    try {
      const response = await InverntroyService.getMarketerOptions();
      if (Array.isArray(response)) {
        setMarketerOptions(response);
      }
      console.log(response);
    } catch (error) {
      console.error("Error loading marketer Options:", error);
      toast.error("Failed to load marketers");
    }
  }, []);

  const loadInvoiceChargesHeads = useCallback(async () => {
    try {
      const response = await InverntroyService.getInvoiceChargesHeads();
      const heads = Array.isArray(response?.data) ? response.data : [];
      setChargesHeads(heads);
      return heads as InvoiceChargesHead[];
    } catch (error) {
      console.error("Error loading invoice charges heads:", error);
      toast.error("Failed to load invoice charges heads");
      return [];
    }
  }, []);

  const loadProductOptions = useCallback(
    async (page: number = 1, searchText: string = "") => {
      try {
        setProductPageInfo((prev) => ({ ...prev, isLoading: true }));

        const response = await InverntroyService.getProductListing({
          page,
          pageSize: 10,
          searchText,
        });

        if (!response || !Array.isArray(response.data)) {
          throw new Error("Invalid product data structure received");
        }

        const productList: OptionType[] = response.data.map((product) => ({
          id: product.id,
          name: product.name || "Unnamed Product",
        }));

        setProductOptions((prev) =>
          page === 1 ? productList : [...prev, ...productList]
        );

        setProductPageInfo((prev) => ({
          ...prev,
          hasMore: Boolean(response.hasMore),
          page,
          isLoading: false,
        }));
      } catch (error) {
        console.error("Error loading products:", error);
        toast.error("Failed to load product options");
        setProductPageInfo((prev) => ({ ...prev, isLoading: false }));
      }
    },
    []
  );

  // Debounce API calls when user types in product search (no fetch at start)
  useEffect(() => {
    if (!productSearchText || productSearchText.trim().length === 0) {
      setProductOptions([]);
      setProductPageInfo((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    setProductPageInfo((prev) => ({ ...prev, isLoading: true }));

    const delayDebounceFn = setTimeout(() => {
      loadProductOptions(1, productSearchText);
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [productSearchText, loadProductOptions]);

  const loadClinicOptions = useCallback(
    async (page: number = 1, searchText: string = "") => {
      try {
        setClinicPageInfo((prev) => ({ ...prev, isLoading: true }));

        const response = await ClinicService.getClinics({
          page,
          pageSize: 100,
          searchText,
        });

        const clinics = Array.isArray(response?.data?.dataList)
          ? response.data.dataList
          : [];

        setClinicOptions((prev) =>
          page === 1 ? clinics : [...prev, ...clinics]
        );

        setClinicPageInfo((prev) => ({
          ...prev,
          hasMore: Boolean(response?.data?.hasMore),
          page,
          isLoading: false,
        }));
      } catch (error) {
        console.error("Error loading clinics:", error);
        toast.error("Failed to load clinic options");
        setClinicPageInfo((prev) => ({ ...prev, isLoading: false }));
      }
    },
    []
  );

  // Click outside handlers
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProductSuggestions(false);
      }
      if (
        wholesalerDropdownRef.current &&
        !wholesalerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowWholesalerSuggestions(false);
      }
      if (
        clinicDropdownRef.current &&
        !clinicDropdownRef.current.contains(event.target as Node)
      ) {
        setShowClinicSuggestions(false);
      }
      if (
        manufacturerDropdownRef.current &&
        !manufacturerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowManufacturerSuggestions(false);
      }
      if (
        marketerDropdownRef.current &&
        !marketerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowMarketerSuggestions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      try {
        await Promise.all([
          loadWholeSalerOptions(),
          loadManufacturerOptions(),
          loadMarketerOptions(),
          loadClinicOptions(1, ""),
          loadInvoiceChargesHeads(),
        ]);
      } catch (error) {
        if (isMounted) {
          console.error("Error initializing data:", error);
          toast.error("Failed to initialize form data");
        }
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [
    loadProductOptions,
    loadClinicOptions,
    loadMarketerOptions,
    loadWholeSalerOptions,
    loadManufacturerOptions,
    loadInvoiceChargesHeads,
  ]);

  const handleProductChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setProductFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Log for debugging
    console.log(`Product field ${name} changed to ${value}`);
  };

  // Effect to load product details when productId changes
  useEffect(() => {
    const loadProductDetails = async () => {
      const productId = productFormData.productId;
      if (!productId) return;

      try {
        const response = await InverntroyService.getProductDetailsInward(productId);
        console.log("response", response);
        if (response && response.success && response.data) {
          const productData = response.data;

          // Update form with product details if available
          setProductFormData((prev) => ({
            ...prev,
            manufacturerId: productData.manufacturerId || prev.manufacturerId,
            hsnNumber: productData.hsnNumber || prev.hsnNumber,
            tabletsPerStripes: productData?.tabletPerStripe,
            cgst: productData.cgst || prev.cgst,
            sgst: productData.sgst || prev.sgst,
            newCgst: productData.newCgst || prev.newCgst,
            newSgst: productData.newSgst || prev.newSgst,
            mrp: productData.mrp?.toString() || prev.mrp,
            categoryId: productData?.category?.id
          }));
        }
      } catch (error) {
        console.error("Error loading product details:", error);
      }
    };

    loadProductDetails();
  }, [productFormData.productId]);

  const getLandingCost = (
    rate: number,
    discount: number,
    noOfItems: number,
    freeItems: number
  ) => {
    const baseAmount = rate * noOfItems;
    const totalItems = noOfItems + freeItems;
    const discountedAmount = baseAmount * (1 - discount / 100);
    //const gstincudedamount=discountedAmount*(1+(cgst+sgst)/100);
    const landingCost = discountedAmount / totalItems;
    return Math.max(0, landingCost);
  };
  const validateExpiryDate = (date: string): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(date);
    expiryDate.setHours(0, 0, 0, 0);
    return expiryDate > today;
  };

  const handleAddProduct = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate invoice data
    for (const [key, value] of Object.entries(invoiceData)) {
      if (value === "" || value === null || value === undefined) {
        toast.error(`Please fill the ${key} field.`);
        return;
      }
    }

    // Validate product data
    for (const [key, value] of Object.entries(productFormData)) {
      if (value === "" || value === null || value === undefined) {
        toast.error(`Please fill the ${key} field.`);
        return;
      }
    }

    if (!validateExpiryDate(productFormData.expiryDate)) {
      toast.error("Expiry date must be a future date");
      return;
    }

    const numberFields = [
      "noOfItems",
      "mrp",
      "freeItems",
      "productRate",
      "discount",
      "cgst",
      "sgst",
    ];

    for (const field of numberFields) {
      if (
        isNaN(Number(productFormData[field as keyof typeof productFormData]))
      ) {
        toast.error(`${field} must be a valid number.`);
        return;
      }
    }

    if (showBonus) {
      if (bonus && bonusQuantity) {
        productFormData.productRate = (
          (Number(productFormData.productRate) * bonusQuantity) /
          (bonusQuantity + bonus)
        ).toFixed(2);
      }
    }

    const landingCost = getLandingCost(
      Number(productFormData.productRate),
      Number(productFormData.discount),
      Number(productFormData.noOfItems),
      Number(productFormData.freeItems)
    );

    const newProduct: Product = {
      id: Date.now().toString(),
      productId: productFormData.productId,
      productName: productFormData.productName,
      manufacturerId: productFormData.manufacturerId,
      marketerId: productFormData.marketerId,
      expiryDate: productFormData.expiryDate,
      batchNumber: productFormData.batchNumber,
      hsnNumber: productFormData.hsnNumber,
      noOfItems: Number(productFormData.noOfItems),
      mrp: Number(productFormData.mrp),
      freeItems: Number(productFormData.freeItems),
      productRate: Number(productFormData.productRate),
      discount: Number(productFormData.discount),
      cgst: Number(productFormData.cgst),
      sgst: Number(productFormData.sgst),
      newCgst: Number(productFormData.newCgst),
      newSgst: Number(productFormData.newSgst),
      landingCost: landingCost ?? 0,
      newMrp: Number(productFormData.newMrp),
      tabletsPerStripes: Number(productFormData.tabletsPerStripes).toString(),
    };

    setProducts((prev) => [...prev, newProduct]);

    setProductFormData({
      productId: "",
      productName: "",
      manufacturerId: "",
      marketerId: "",
      expiryDate: "",
      batchNumber: "",
      hsnNumber: "",
      tabletsPerStripes: "",
      noOfItems: "",
      mrp: "",
      freeItems: "",
      productRate: "",
      discount: "",
      cgst: "",
      sgst: "",
      newCgst: "",
      newSgst: "",
      newMrp: "",
      categoryId: ""
    });

    setProductSearchText("");
    setManufacturerSearchText("");
    setMarketerSearchText("");

    toast.success("Product added to order list");
  };

  const clearAllFields = () => {
    setInvoiceData({
      wholesalerId: "",
      clinicId: "",
      invoiceNumber: "",
      invoiceDate: "",
      invoiceAmount: "",
      payment_status: "completed",
    });

    setProductFormData({
      productId: "",
      manufacturerId: "",
      productName: "",
      marketerId: "",
      expiryDate: "",
      batchNumber: "",
      hsnNumber: "",
      tabletsPerStripes: "",
      noOfItems: "",
      mrp: "",
      freeItems: "",
      productRate: "",
      discount: "",
      cgst: "",
      sgst: "",
      newCgst: "",
      newSgst: "",
      newMrp: "",
      categoryId: ""
    });

    setBonus(0);
    setShowBonus(false);
    setBonusQuantity(0);

    setProducts([]);
    setMasterDiscountAmount(0);
    setInitialGrossTotal(0);
    setInitialGst(0);
    setRoundOff(0);
    setRoundOffAmount(0);
    setShowAddDiscountButton(true);
    setInvoiceCharges([]);
    setProductSearchText("");
    setManufacturerSearchText("");
  };

  const deleteProduct = (productId: string) => {
    setProducts((prev) => prev.filter((p) => p.id !== productId));

    toast.success("Product removed from order");
  };

  // Update Added products
  const updateProducts = (productId: string) => {
    const productToEdit = products.find((p) => p.productId === productId);
    if (!productToEdit) {
      toast.error("Product not found");
      return;
    }

    console.log("Product to edit", productToEdit);

    setProductFormData({
      productId: productToEdit.productId,
      productName: productToEdit.productName,
      manufacturerId: productToEdit.manufacturerId,
      marketerId: productToEdit.marketerId,
      expiryDate: productToEdit.expiryDate,
      batchNumber: productToEdit.batchNumber,
      hsnNumber: productToEdit.hsnNumber,
      tabletsPerStripes: productToEdit.tabletsPerStripes,
      noOfItems: productToEdit.noOfItems.toString(),
      mrp: productToEdit.mrp.toString(),
      freeItems: productToEdit.freeItems.toString(),
      productRate: productToEdit.productRate.toString(),
      discount: productToEdit.discount.toString(),
      cgst: productToEdit.cgst.toString(),
      sgst: productToEdit.sgst.toString(),
      newCgst: productToEdit.newCgst.toString(),
      newSgst: productToEdit.newSgst.toString(),
      newMrp: productToEdit?.newMrp?.toString(),
      categoryId: productToEdit.categoryId
    });

    setIsEditMode(true);
    setEditingProductId(productToEdit.id);
    setProductSearchText(
      productOptions.find((p) => p.id === productToEdit.productId)?.name ||
      "Product"
    );
    setManufacturerSearchText(
      manufacturerOptions.find((p) => p.id === productToEdit.manufacturerId)
        ?.manufacturerName || "Manufacturer"
    );
    setMarketerSearchText(
      marketerOptions.find((p) => p.id === productToEdit.marketerId)
        ?.marketerName || "Marketer"
    );
  };

  const handleUpdateProduct = (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingProductId) {
      toast.error("No product selected for update.");
      return;
    }

    // Validate similar to handleAddProduct...
    const numberFields = [
      "noOfItems",
      "mrp",
      "freeItems",
      "productRate",
      "discount",
      "cgst",
      "sgst",
    ];

    for (const field of numberFields) {
      if (
        isNaN(Number(productFormData[field as keyof typeof productFormData]))
      ) {
        toast.error(`${field} must be a valid number.`);
        return;
      }
    }

    if (showBonus) {
      if (bonus && bonusQuantity) {
        productFormData.productRate = (
          (Number(productFormData.productRate) * bonusQuantity) /
          (bonusQuantity + bonus)
        ).toFixed(2);
      }
    }

    const landingCost = getLandingCost(
      Number(productFormData.productRate),
      Number(productFormData.discount),
      Number(productFormData.noOfItems),
      Number(productFormData.freeItems)
    );

    const updatedProduct: Product = {
      id: editingProductId,
      productId: productFormData.productId,
      productName: productFormData.productName,
      manufacturerId: productFormData.manufacturerId,
      marketerId: productFormData.marketerId,
      expiryDate: productFormData.expiryDate,
      batchNumber: productFormData.batchNumber,
      hsnNumber: productFormData.hsnNumber,
      tabletsPerStripes: productFormData.tabletsPerStripes,
      noOfItems: Number(productFormData.noOfItems),
      mrp: Number(productFormData.mrp),
      freeItems: Number(productFormData.freeItems),
      productRate: Number(productFormData.productRate),
      discount: Number(productFormData.discount),
      cgst: Number(productFormData.cgst),
      sgst: Number(productFormData.sgst),
      newCgst: Number(productFormData.newCgst),
      newSgst: Number(productFormData.newSgst),
      newMrp: Number(productFormData.newMrp),
      landingCost,
    };

    setProducts((prev) =>
      prev.map((product) =>
        product.id === editingProductId ? updatedProduct : product
      )
    );

    setProductFormData({
      productId: "",
      productName: "",
      manufacturerId: "",
      marketerId: "",
      expiryDate: "",
      batchNumber: "",
      hsnNumber: "",
      tabletsPerStripes: "",
      noOfItems: "",
      mrp: "",
      freeItems: "",
      productRate: "",
      discount: "",
      cgst: "",
      sgst: "",
      newCgst: "",
      newSgst: "",
      newMrp: '',
      categoryId: ''
    });

    setIsEditMode(false);
    setEditingProductId(null);
    toast.success("Product updated successfully");
  };

  useEffect(() => {
    if (products.length === 0) {
      setMasterDiscountAmount(0);
      setShowAddDiscountButton(true);
      setInitialGrossTotal(0);
      setInitialGst(0);
      setRoundOff(0);
      setRoundOffAmount(0);
      setInvoiceCharges([]);
    }
  }, [products]);
  const applyDiscountToProducts = (
    products: Product[],
    masterDiscountAmount: number
  ): Product[] => {
    if (products.length === 0 || masterDiscountAmount < 0) return products;

    const totalValue = products.reduce(
      (sum, product) => sum + product.productRate * product.noOfItems,
      0
    );

    if (totalValue <= 0) return products;

    const discountRatio = masterDiscountAmount / totalValue;

    return products.map((product) => {
      const productValue = product.productRate * product.noOfItems;
      const productDiscountAmount = productValue * discountRatio;
      const productDiscountPercentage =
        (productDiscountAmount / productValue) * 100;

      const landingCost = getLandingCost(
        product.productRate,
        productDiscountPercentage,
        product.noOfItems,
        product.freeItems
      );

      return {
        ...product,
        discount: productDiscountPercentage,
        landingCost,
      };
    });
  };

  const applyDiscountToAllProducts = () => {
    const amount = parseFloat((masterDiscountAmountTemp || 0).toString());
    if (isNaN(amount) || amount < 0) {
      toast.error("Please enter a valid discount amount");
      return;
    }

    if (amount > initialGrossTotal) {
      toast.error("Discount amount cannot be greater than gross total");
      return;
    }
    setMasterDiscountAmount(masterDiscountAmountTemp || 0);
    const updatedProducts = applyDiscountToProducts(products, amount);
    setProducts(updatedProducts);
    setIsDiscountModalOpen(false);

    toast.success("Discount applied successfully");
  };

  const handleAddInvoiceCharge = () => {
    const headId = Number(selectedHeadId);
    const head = getChargeHead(headId);
    if (!head) {
      toast.error("Please select a charges head");
      return;
    }

    const amount = parseFloat(extraChargeAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    // One entry per head; adding the same head again replaces its amount
    const updatedCharges = [
      ...invoiceCharges.filter((charge) => charge.headId !== headId),
      { headId, amount },
    ];
    const updatedNetCharge = updatedCharges.reduce(
      (sum, charge) => sum + getSignedChargeAmount(charge),
      0
    );
    const orderValue = initialGrossTotal - Number(calculateDiscountTotal());
    if (orderValue + updatedNetCharge < 0) {
      toast.error("Discount charges cannot be greater than the order value");
      return;
    }

    setInvoiceCharges(updatedCharges);
    setExtraChargeAmount("");
    toast.success(`${head.headType} applied`);
  };

  const removeInvoiceCharge = (headId: number) => {
    setInvoiceCharges((prev) =>
      prev.filter((charge) => charge.headId !== headId)
    );
  };

  const handleCreateChargesHead = async () => {
    const headType = newHeadForm.headType.trim();
    if (!headType) {
      toast.error("Please enter a head name");
      return;
    }

    const headGst =
      newHeadForm.headGst.trim() === "" ? null : Number(newHeadForm.headGst);
    if (headGst !== null && (isNaN(headGst) || headGst < 0 || headGst > 100)) {
      toast.error("GST must be between 0 and 100");
      return;
    }

    setIsSavingHead(true);
    try {
      const response = await InverntroyService.createInvoiceChargesHead({
        headType,
        headHsnNumber: newHeadForm.headHsnNumber.trim() || null,
        headGst,
        action: newHeadForm.action,
      });

      if (!response?.success) {
        toast.error(response?.message || "Failed to create charges head");
        return;
      }

      const heads = await loadInvoiceChargesHeads();
      const createdId =
        response.data?.id ??
        heads.find((head) => head.headType === headType)?.id;
      if (createdId) setSelectedHeadId(createdId.toString());

      setNewHeadForm({
        headType: "",
        headHsnNumber: "",
        headGst: "",
        action: "increase",
      });
      setIsNewHeadFormOpen(false);
      toast.success("Charges head created");
    } finally {
      setIsSavingHead(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (products.length === 0) {
      toast.error("Please add at least one product");
      return;
    }

    if (isInvoiceValid === false) {
      toast.error("Please fix invoice number validation issues");
      return;
    }

    if (isNaN(Number(invoiceData.invoiceAmount))) {
      toast.error("Invoice amount must be a valid number.");
      return;
    }
    const invoiceAmount = parseFloat(invoiceData.invoiceAmount);

    if (isNaN(invoiceAmount)) {
      toast.error("Invalid invoice amount");
      return;
    }

    if (Math.abs(roundOffAmount - invoiceAmount) > 0.01) {
      toast.error("Invoice amount and net amount does not match");
      console.log("netAmount, invoiceAmount", roundOffAmount, invoiceAmount);
      return;
    }

    console.log("Products", products)
    try {
      const submitDataArray = products.map((product) => ({
        wholesalerId: invoiceData.wholesalerId,
        clinicId: invoiceData.clinicId,
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: new Date(invoiceData.invoiceDate).toISOString(),
        invoiceAmount: Number(invoiceData.invoiceAmount),
        payment_status: invoiceData.payment_status || "completed",
        productId: product.productId,
        manufacturerId: product.manufacturerId,
        marketerId: product.marketerId,
        expiryDate: new Date(product.expiryDate).toISOString(),
        batchNumber: product.batchNumber,
        hsnNumber: product.hsnNumber,
        noOfItems: product.noOfItems,
        mrp: useOldGst ? product.newMrp : product.mrp,
        freeItems: product.freeItems,
        productRate: product.productRate,
        discount: product.discount,
        cgst: product.newCgst,
        sgst: product.newSgst,
        // Per unit share of the invoice charges for this product
        ...getExtraHeadPayload(product),
      }));

      const response = await InverntroyService.addProductToStock({
        paymentStatus: invoiceData.payment_status || "completed",
        data: submitDataArray,
        invoiceBreakdownCharges: invoiceCharges.map((charge) => ({
          invoiceNumber: invoiceData.invoiceNumber.trim(),
          headId: charge.headId,
          // Amount entered by the admin, without the head GST
          headAmount: Number(charge.amount.toFixed(2)),
        })),
      });

      if (!response.success) {
        throw new Error(response.message || "Failed to add products");
      }

      toast.success("All products added to inventory successfully");

      clearAllFields();
      onClose();
    } catch (error) {
      console.error("Error submitting products:", error);
      toast.error("Failed to submit products");
    }
  };

  return (
    <div className="flex w-full">
      <div className="w-full">
        <div className="flex items-center mb-4 justify-between">
          <div className="flex items-center mb-4">
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <CircleChevronLeft className="w-6 h-6 bg-[#808080] text-white rounded-full mr-2" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              Inward New Stock
            </h2>
          </div>
        </div>

        <div className="border border-gray-300 bg-white rounded-xl mt-3 p-4">
          <div className="flex space-x-4 mb-4 justify-between">
            {/* Updated Wholesaler Search Field */}
            <div className="flex-1">
              <SearchableDropdown
                label="Wholesaler"
                placeholder="Search for a wholesaler"
                searchText={wholesalerSearchText}
                setSearchText={setWholesalerSearchText}
                suggestionsVisible={showWholesalerSuggestions}
                setSuggestionsVisible={setShowWholesalerSuggestions}
                options={wholeSalerOptions.map((w) => ({
                  id: w.id,
                  label: w.wholeSalerName,
                }))}
                onSelect={(selected) => {
                  setInvoiceData((prev) => ({
                    ...prev,
                    wholesalerId: selected.id,
                  }));
                  setWholesalerSearchText(selected.label);
                }}
                dropdownRef={wholesalerDropdownRef}
              />
              {invoiceData.wholesalerId && (
                <div className="text-sm text-green-600 mt-1">
                  Selected:{" "}
                  {wholeSalerOptions.find(
                    (p) => p.id === invoiceData.wholesalerId
                  )?.wholeSalerName || "Wholesaler"}
                </div>
              )}
            </div>

            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Invoice Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="invoiceNumber"
                  value={invoiceData.invoiceNumber}
                  onChange={handleInvoiceChange}
                  onBlur={handleInvoiceBlur}
                  className={`w-full p-2 pr-10 border ${isInvoiceValid === false
                    ? "border-red-500 focus:ring-red-500"
                    : isInvoiceValid === true
                      ? "border-green-500 focus:ring-green-500"
                      : "border-gray-300 focus:ring-green-500"
                    } rounded focus:outline-none focus:ring-1`}
                  placeholder="Enter Invoice Number"
                  disabled={isCheckingInvoice}
                />
                {isCheckingInvoice && (
                  <div className="absolute right-2 top-2.5 h-4 w-4 border-2 border-gray-400 border-t-2 border-t-blue-500 rounded-full animate-spin"></div>
                )}
                {isInvoiceValid === true && !isCheckingInvoice && (
                  <svg
                    className="absolute right-2 top-2.5 h-5 w-5 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
                {isInvoiceValid === false && !isCheckingInvoice && (
                  <svg
                    className="absolute right-2 top-2.5 h-5 w-5 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Invoice Date
              </label>
              <input
                type="date"
                name="invoiceDate"
                value={invoiceData.invoiceDate}
                onChange={handleInvoiceChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Invoice Amount
              </label>
              <input
                type="text"
                name="invoiceAmount"
                value={invoiceData.invoiceAmount}
                onChange={handleInvoiceChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Enter Invoice Amount"
              />
            </div>

            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Payment Status
              </label>
              <select
                name="payment_status"
                value={invoiceData.payment_status}
                onChange={handleInvoiceChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 bg-white"
              >
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* Updated Clinic Search Field */}
            <div className="flex-1">
              <SearchableDropdown
                label="Select Clinic"
                searchText={clinicSearchText}
                setSearchText={setClinicSearchText}
                suggestionsVisible={showClinicSuggestions}
                setSuggestionsVisible={setShowClinicSuggestions}
                options={clinicOptions.map((c) => ({
                  id: c.id,
                  label: c.name,
                }))}
                onSelect={(selected) => {
                  setInvoiceData((prev) => ({
                    ...prev,
                    clinicId: selected.id,
                  }));
                  setClinicSearchText(selected.label);
                }}
                dropdownRef={clinicDropdownRef}
              />

              {invoiceData.clinicId && (
                <div className="text-sm text-green-600 mt-1">
                  Selected:{" "}
                  {
                    clinicOptions.find((c) => c.id === invoiceData.clinicId)
                      ?.name
                  }
                </div>
              )}
            </div>
          </div>

          {/* product information */}
          <div className="text-green-600 font-semibold mb-4 mt-6">
            PRODUCT INFORMATION
          </div>

          <div className="flex gap-4 w-full">
            <div className="w-full gap-4">
              {/* Product Name */}
              <div className="mb-4 relative">
                <SearchableDropdown
                  label="Product Name"
                  searchText={productSearchText}
                  setSearchText={setProductSearchText}
                  suggestionsVisible={showProductSuggestions}
                  setSuggestionsVisible={setShowProductSuggestions}
                  options={productOptions.map((p) => ({
                    id: p.id,
                    label: p.name,
                  }))}
                  onSelect={(selected) => {
                    setProductFormData((prev) => ({
                      ...prev,
                      productId: selected.id,
                      productName: selected.label,
                    }));
                    setProductSearchText(selected.label);
                  }}
                  dropdownRef={productDropdownRef}
                  isLoading={productPageInfo.isLoading}
                  disableClientFilter={true}
                />

                {productFormData.productId && (
                  <div className="text-sm text-green-600 mt-1">
                    Selected:{" "}
                    {
                      productOptions.find(
                        (p) => p.id === productFormData.productId
                      )?.name || productFormData.productName
                    }
                  </div>
                )}
              </div>

              <div className="flex gap-4 mb-4 justify-between">
                {/* Manufacturer Search Field */}
                <div className="flex-1 relative">
                  <SearchableDropdown
                    label="Manufacturer"
                    searchText={manufacturerSearchText}
                    setSearchText={setManufacturerSearchText}
                    suggestionsVisible={showManufacturerSuggestions}
                    setSuggestionsVisible={setShowManufacturerSuggestions}
                    options={manufacturerOptions.map((m) => ({
                      id: m.id,
                      label: m.manufacturerName,
                    }))}
                    onSelect={(selected) => {
                      setProductFormData((prev) => ({
                        ...prev,
                        manufacturerId: selected.id,
                      }));
                      setManufacturerSearchText(selected.label);
                    }}
                    dropdownRef={manufacturerDropdownRef}
                  />

                  {productFormData.manufacturerId && (
                    <div className="text-sm text-green-600 mt-1">
                      Selected:{" "}
                      {
                        manufacturerOptions.find(
                          (m) => m.id === productFormData.manufacturerId
                        )?.manufacturerName
                      }
                    </div>
                  )}
                </div>
                {/* Marketer Search Field */}
                <div className="flex-1 relative">
                  <SearchableDropdown
                    label="Marketer"
                    searchText={marketerSearchText}
                    setSearchText={setMarketerSearchText}
                    suggestionsVisible={showMarketerSuggestions}
                    setSuggestionsVisible={setShowMarketerSuggestions}
                    options={marketerOptions.map((m) => ({
                      id: m.id,
                      label: m.marketerName,
                    }))}
                    onSelect={(selected) => {
                      setProductFormData((prev) => ({
                        ...prev,
                        marketerId: selected.id,
                      }));
                      setMarketerSearchText(selected.label);
                    }}
                    dropdownRef={marketerDropdownRef}
                  />

                  {productFormData.marketerId && (
                    <div className="text-sm text-green-600 mt-1">
                      Selected:{" "}
                      {
                        marketerOptions.find(
                          (m) => m.id === productFormData.marketerId
                        )?.marketerName
                      }
                    </div>
                  )}
                </div>
                {/* HSN Number */}
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    HSN Number
                  </label>
                  <input
                    type="text"
                    name="hsnNumber"
                    value={productFormData.hsnNumber}
                    onChange={handleProductChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="HSN Number"
                    disabled
                  />
                </div>
                {/* Batch Number */}
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Batch Number
                  </label>
                  <input
                    type="text"
                    name="batchNumber"
                    value={productFormData.batchNumber}
                    onChange={handleProductChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="Batch Number"
                  />
                </div>
              </div>
              <div className="flex gap-4 mb-4 justify-between">
                {/* Quantity */}
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Quantity
                  </label>
                  <input
                    type="text"
                    name="noOfItems"
                    value={productFormData.noOfItems}
                    onChange={handleProductChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="Enter Number of quantities"
                  />
                </div>

                {/* Free Quantity */}
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Free Quantity
                  </label>
                  <input
                    type="text"
                    name="freeItems"
                    value={productFormData.freeItems}
                    onChange={handleProductChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="Enter Free Quantity"
                  />
                </div>

                {/* Expiry Date */}
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="month"
                    name="expiryDate"
                    value={
                      productFormData.expiryDate
                        ? productFormData.expiryDate.slice(0, 7)
                        : ""
                    }
                    onChange={(e) => {
                      // Convert month-year input to full date (first day of month)
                      const monthYearValue = e.target.value;
                      const fullDate = monthYearValue
                        ? `${monthYearValue}-01`
                        : "";

                      // Call your handler with the synthetic event
                      handleProductChange({
                        target: {
                          name: e.target.name,
                          value: fullDate,
                        },
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>
              </div>
              <div>
                {/* Stripes per tablet */}
                {productFormData.tabletsPerStripes &&
                  productFormData.tabletsPerStripes !== "" && (
                    <div className="flex-1">
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Tablet per strip
                      </label>
                      <input
                        type="text"
                        name="hsnNumber"
                        value={productFormData.tabletsPerStripes}
                        onChange={handleProductChange}
                        className="w-[20%] p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        placeholder="Tablet per Strip"
                        disabled
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>

          {!showBonus && (
            <button
              onClick={() => setShowBonus(true)}
              className="text-green-600"
            >
              + Add Bonus
            </button>
          )}
          {showBonus && (
            <div className="text-green-600 font-semibold mb-4 mt-4">
              Bonus Quantities
              <button
                onClick={() => setShowBonus(false)}
                className="text-red-600 mx-4"
              >
                {" "}
                Remove{" "}
              </button>
            </div>
          )}
          {showBonus && (
            <div className="flex flex-col gap-4 w-full">
              <div className="flex space-x-4 mb-4 justify-between">
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Quantity
                  </label>
                  <input
                    type="text"
                    name="bonusQuantity"
                    value={bonusQuantity}
                    onChange={(e) => setBonusQuantity(Number(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="Enter Bonus Quantity"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Bonus
                  </label>
                  <input
                    type="text"
                    name="bonus"
                    value={bonus}
                    onChange={(e) => setBonus(Number(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="Enter Bonus"
                  />
                </div>
              </div>
            </div>
          )}

          <div className=" flex justify-between items-center my-4">
            <p className="text-green-600 font-semibold ">Product GST</p>
            {/* <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-green-600">
                Use Old GST
              </span>
              <button
                onClick={() => setUseOldGst(!useOldGst)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  useOldGst ? "bg-green-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    useOldGst ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div> */}
          </div>

          <div className="flex items-center gap-6 mb-5">
            <label
              className={`${formStyles.lableStyle} flex items-center gap-1`}
            >
              Use Old GST?<span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rxRequired"
                  value="yes"
                  checked={useOldGst === true}
                  onChange={() => setUseOldGst(true)}
                  className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                  required
                />
                <span className="text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rxRequired"
                  value="no"
                  checked={useOldGst === false}
                  onChange={() => setUseOldGst(false)}
                  className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                  required
                />
                <span className="text-sm text-gray-700">No</span>
              </label>
            </div>
            {useOldGst === undefined && (
              <p className="text-red-500 text-xs mt-1">
                Please select an option
              </p>
            )}
          </div>

          <div className="flex gap-4 w-full">
            {/* SGST */}
            {useOldGst && <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                SGST
              </label>
              <input
                type="text"
                name="sgst"
                value={productFormData.sgst}
                onChange={handleProductChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Prev SGST"
                disabled
              />
            </div>}
            {/* CGST */}
            {useOldGst && <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                CGST
              </label>
              <input
                type="text"
                name="cgst"
                value={productFormData.cgst}
                onChange={handleProductChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Prev CGST"
                disabled
              />
            </div>}

            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                New SGST
              </label>
              <input
                type="text"
                name="sgst"
                value={productFormData.newSgst}
                onChange={handleProductChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="New SGST"
                disabled
              />
            </div>

            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                New CGST
              </label>
              <input
                type="text"
                name="cgst"
                value={productFormData.newCgst}
                onChange={handleProductChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="New CGST"
                disabled
              />
            </div>
          </div>

          {/* prices */}
          <div className="text-green-600 font-semibold mb-4 mt-4">PRICES</div>
          <div className="flex flex-col gap-4 w-full">
            <div className="flex space-x-4 mb-4 justify-between">
              {/* Product M.R.P */}
              <div className="flex-1">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Product M.R.P
                </label>
                <input
                  type="text"
                  name="mrp"
                  value={productFormData.mrp}
                  onChange={handleProductChange}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                  placeholder="Enter Product M.R.P"
                />
              </div>

              {useOldGst && (
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    New M.R.P
                  </label>
                  <input
                    type="text"
                    name="mrp"
                    value={productFormData.newMrp}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="Enter Product M.R.P"
                    disabled
                  />
                </div>
              )}

              {/* Product Rate */}
              <div className="flex-1">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Product Rate
                </label>
                <input
                  type="text"
                  name="productRate"
                  value={productFormData.productRate}
                  onChange={handleProductChange}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                  placeholder="Enter Product Rate"
                />
              </div>
              {/* Discount */}
              <div className="flex-1">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Wholesaler Discount %{" "}
                </label>
                <input
                  type="text"
                  name="discount"
                  value={productFormData.discount}
                  onChange={handleProductChange}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                  placeholder="Enter Wholesaler Discount"
                />
              </div>
            </div>
          </div>

          {/* add product button */}
          <div className="mt-4 mb-4">
            <div className="flex justify-start mt-4">
              <button
                onClick={isEditMode ? handleUpdateProduct : handleAddProduct}
                className="bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 transition"
              >
                {isEditMode ? "Update Product" : "Add Product"}
              </button>
            </div>
          </div>
        </div>

        {/* order details and invoice details */}
        <div className="bg-white rounded-xl p-6 mt-6 mb-12 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-6 items-stretch">
            {/* ORDER DETAILS */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="text-green-600 font-semibold mb-3">
                ORDER DETAILS
              </div>
              <div className="flex-1 min-h-[300px] md:min-h-[350px] border border-gray-200 rounded-xl shadow-sm p-4 overflow-auto">
                {products.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="p-2 text-left">Product</th>
                          <th className="p-2 text-left">HSN</th>
                          <th className="p-2 text-left">Mfg</th>
                          <th className="p-2 text-left">Qty</th>
                          <th className="p-2 text-left">Free</th>
                          <th className="p-2 text-left">Batch</th>
                          <th className="p-2 text-left">Expiry</th>
                          <th className="p-2 text-left">MRP</th>
                          <th className="p-2 text-left">Rate</th>
                          <th className="p-2 text-left">Disc Amt</th>{" "}
                          {/* Changed from Disc% */}
                          <th className="p-2 text-left">Extra Head</th>
                          <th className="p-2 text-left">SGST%</th>
                          <th className="p-2 text-left">CGST%</th>
                          <th className="p-2 text-left">Landing Cost</th>
                          <th className="p-2 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product) => {
                          // Calculate discount amount for each product
                          const discountAmount =
                            (product.productRate *
                              product.noOfItems *
                              product.discount) /
                            100;
                          const extraChargeShare = allocateByQuantity(
                            netExtraCharge,
                            product
                          );

                          return (
                            <tr key={product.id} className="border-b">
                              <td className="p-2">{product.productName}</td>
                              <td className="p-2">{product.hsnNumber}</td>
                              <td className="p-2">
                                {manufacturerOptions.find(
                                  (m) => m.id === product.manufacturerId
                                )?.manufacturerName || product.manufacturerId}
                              </td>
                              <td className="p-2">{product.noOfItems}</td>
                              <td className="p-2">{product.freeItems}</td>
                              <td className="p-2">{product.batchNumber}</td>
                              <td className="p-2">
                                {product.expiryDate
                                  ? (() => {
                                    const date = new Date(product.expiryDate);
                                    const month = String(
                                      date.getMonth() + 1
                                    ).padStart(2, "0"); // Months are 0-indexed
                                    const year = String(
                                      date.getFullYear()
                                    ).slice(-2); // Get last 2 digits of year
                                    return `${month}/${year}`;
                                  })()
                                  : "N/A"}
                              </td>
                              <td className="p-2">₹{!useOldGst ? product.mrp.toFixed(2) : product.newMrp.toFixed(2)}</td>
                              <td className="p-2">
                                ₹{product.productRate.toFixed(2)}
                              </td>
                              <td className="p-2">
                                ₹{discountAmount.toFixed(2)}
                              </td>{" "}
                              {/* Changed from percentage to amount */}
                              <td
                                className={`p-2 whitespace-nowrap ${extraChargeShare < 0 ? "text-red-600" : ""}`}
                              >
                                {extraChargeShare < 0 ? "-" : ""}₹
                                {Math.abs(extraChargeShare).toFixed(2)}
                                <span className="block text-xs text-gray-500">
                                  ₹
                                  {Math.abs(
                                    getExtraHeadPerUnit(product)
                                  ).toFixed(2)}{" "}
                                  / unit
                                </span>
                              </td>
                              <td className="p-2">{product.sgst}%</td>
                              <td className="p-2">{product.cgst}%</td>
                              <td className="p-2">
                                ₹{getFinalLandingCost(product).toFixed(2)}
                              </td>
                              <td className="p-2">
                                <button
                                  onClick={() =>
                                    updateProducts(product.productId)
                                  }
                                  className="text-green-600 hover:text-green-800"
                                  title="Edit product"
                                >
                                  <SquarePen className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => deleteProduct(product.id)}
                                  className="text-red-500 hover:text-red-700"
                                  title="Remove product"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No products added yet
                  </div>
                )}
              </div>
            </div>

            {/* INVOICE DETAILS */}
            <div className="md:basis-1/3 w-full flex flex-col min-w-0">
              <div className="text-green-600 font-semibold mb-3">
                INVOICE DETAILS
              </div>
              <div className="flex-1 min-h-[300px] md:min-h-[350px] border border-gray-200 rounded-xl shadow-sm p-4 flex flex-col justify-between h-full overflow-auto">
                <div>
                  <div className="flex justify-between items-center px-1 py-2 border-b border-gray-300">
                    <span>Gross Total</span>
                    <span>₹ {initialGrossTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center px-1 py-2 border-b border-gray-300">
                    <span>Less Discount</span>
                    <span className="text-red-600">
                      -₹ {calculateDiscountTotal()}
                    </span>
                  </div>
                  {invoiceCharges.map((charge) => {
                    const head = getChargeHead(charge.headId);
                    const isDeduction = head?.action === "discount";
                    return (
                      <div
                        key={charge.headId}
                        className="flex justify-between items-center px-1 py-2 border-b border-gray-300"
                      >
                        <span>
                          {isDeduction ? "Less " : "Add "}
                          {head?.headType ?? "Charge"}
                          {head?.headGst ? ` (incl. ${head.headGst}% GST)` : ""}
                        </span>
                        <span className={isDeduction ? "text-red-600" : ""}>
                          {isDeduction ? "-" : ""}₹{" "}
                          {getChargeNetAmount(charge).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center px-1 py-2 border-b border-gray-300">
                    <span>Add GST</span>
                    <span>₹ {initialGst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center px-1 py-2 border-b border-gray-300">
                    <span>Less CR / DR</span>
                    <span>₹ 0.00</span>
                  </div>
                  <div className="flex justify-between items-center px-1 py-2">
                    <span>Round Off</span>
                    <span>₹ {roundOff}</span>
                  </div>
                </div>
                <div className="-mx-4 flex justify-between items-center pt-4 border-t font-semibold text-base">
                  <span className="px-4">NET AMOUNT</span>
                  <span className="text-green-700 font-bold px-4">
                    ₹ {roundOffAmount}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 mt-6">
            <button
              onClick={handleFinalSubmit}
              disabled={isSubmitting}
              className={`bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center min-w-32 ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                "Add to Inventory"
              )}
            </button>
            {products.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-3">
                {showAddDiscountButton && (
                  <button
                    onClick={() => setIsDiscountModalOpen(true)}
                    className="border border-green-600 text-green-600 px-5 py-2 rounded-lg hover:bg-green-50 transition"
                  >
                    Add Discount
                  </button>
                )}
                <button
                  onClick={() => setIsExtraChargeModalOpen(true)}
                  className="border border-green-600 text-green-600 px-5 py-2 rounded-lg hover:bg-green-50 transition"
                >
                  Add Invoice Charges
                </button>
              </div>
            )}
          </div>

          {/* Invoice Charges Modal */}
          {isExtraChargeModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-800">
                    Add Invoice Charges
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Amount with GST is distributed across products based on
                    their quantity
                  </p>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Charges Head */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-700 font-medium">
                      Charges Head
                    </span>
                    <button
                      onClick={() => setIsNewHeadFormOpen((prev) => !prev)}
                      className="text-green-600 text-sm hover:text-green-700"
                    >
                      {isNewHeadFormOpen ? "Cancel" : "+ Add New Head"}
                    </button>
                  </div>
                  <select
                    value={selectedHeadId}
                    onChange={(e) => setSelectedHeadId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white mb-4"
                  >
                    <option value="">Select a charges head</option>
                    {chargesHeads.map((head) => (
                      <option key={head.id} value={head.id}>
                        {head.headType}
                        {head.headGst ? ` · ${head.headGst}% GST` : ""} ·{" "}
                        {head.action === "discount" ? "Discount" : "Increase"}
                      </option>
                    ))}
                  </select>

                  {/* New Head Form */}
                  {isNewHeadFormOpen && (
                    <div className="border border-gray-200 rounded-xl p-4 mb-4 space-y-3">
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">
                          Head Name
                        </label>
                        <input
                          type="text"
                          value={newHeadForm.headType}
                          onChange={(e) =>
                            setNewHeadForm((prev) => ({
                              ...prev,
                              headType: e.target.value,
                            }))
                          }
                          maxLength={100}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                          placeholder="e.g. Freight"
                        />
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="block text-gray-700 text-sm font-medium mb-1">
                            HSN Number
                          </label>
                          <input
                            type="text"
                            value={newHeadForm.headHsnNumber}
                            onChange={(e) =>
                              setNewHeadForm((prev) => ({
                                ...prev,
                                headHsnNumber: e.target.value,
                              }))
                            }
                            maxLength={50}
                            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder="Optional"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-gray-700 text-sm font-medium mb-1">
                            GST %
                          </label>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={newHeadForm.headGst}
                            onChange={(e) =>
                              setNewHeadForm((prev) => ({
                                ...prev,
                                headGst: e.target.value,
                              }))
                            }
                            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <span className="text-gray-700 text-sm font-medium">
                          Action
                        </span>
                        {(["increase", "discount"] as ChargeHeadAction[]).map(
                          (action) => (
                            <label
                              key={action}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="headAction"
                                checked={newHeadForm.action === action}
                                onChange={() =>
                                  setNewHeadForm((prev) => ({
                                    ...prev,
                                    action,
                                  }))
                                }
                                className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                              />
                              <span className="text-sm text-gray-700 capitalize">
                                {action}
                              </span>
                            </label>
                          )
                        )}
                      </div>
                      <button
                        onClick={handleCreateChargesHead}
                        disabled={isSavingHead}
                        className={`w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium transition-colors ${isSavingHead ? "opacity-75 cursor-not-allowed" : ""
                          }`}
                      >
                        {isSavingHead ? "Saving..." : "Save Head"}
                      </button>
                    </div>
                  )}

                  {/* Amount Input */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-700 font-medium">
                      Amount (₹), before GST
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="number"
                      min={0}
                      value={extraChargeAmount}
                      onChange={(e) => setExtraChargeAmount(e.target.value)}
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                      placeholder="0.00"
                    />
                    <button
                      onClick={handleAddInvoiceCharge}
                      className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 font-medium transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {/* Net preview for the head being added */}
                  {selectedHeadId && Number(extraChargeAmount) > 0 && (
                    <p className="text-sm text-gray-500 mb-6">
                      {getChargeHead(Number(selectedHeadId))?.headGst
                        ? `GST ${getChargeHead(Number(selectedHeadId))?.headGst}% · `
                        : "No GST · "}
                      Net ₹{" "}
                      {getChargeNetAmount({
                        headId: Number(selectedHeadId),
                        amount: Number(extraChargeAmount),
                      }).toFixed(2)}
                    </p>
                  )}

                  {/* Applied Charges */}
                  <div className="bg-gray-50 rounded-xl p-4 my-6">
                    {invoiceCharges.length > 0 ? (
                      <div className="space-y-3">
                        {invoiceCharges.map((charge) => {
                          const head = getChargeHead(charge.headId);
                          const isDeduction = head?.action === "discount";
                          return (
                            <div
                              key={charge.headId}
                              className="flex justify-between items-center"
                            >
                              <span className="text-gray-600">
                                {head?.headType ?? "Charge"}
                                <span className="block text-xs text-gray-400">
                                  ₹ {charge.amount.toFixed(2)}
                                  {head?.headGst
                                    ? ` + ${head.headGst}% GST`
                                    : ""}
                                </span>
                              </span>
                              <div className="flex items-center gap-3">
                                <span
                                  className={`font-medium ${isDeduction ? "text-red-600" : ""
                                    }`}
                                >
                                  {isDeduction ? "-" : "+"}₹{" "}
                                  {getChargeNetAmount(charge).toFixed(2)}
                                </span>
                                <button
                                  onClick={() =>
                                    removeInvoiceCharge(charge.headId)
                                  }
                                  className="text-red-500 hover:text-red-700 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                          <span className="text-gray-800 font-semibold">
                            Net Adjustment
                          </span>
                          <span
                            className={`font-bold ${netExtraCharge < 0
                              ? "text-red-600"
                              : "text-green-600"
                              }`}
                          >
                            {netExtraCharge < 0 ? "-" : "+"}₹{" "}
                            {Math.abs(netExtraCharge).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm text-center">
                        No invoice charges added yet
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      setIsExtraChargeModalOpen(false);
                      setExtraChargeAmount("");
                      setIsNewHeadFormOpen(false);
                    }}
                    className="w-full px-6 py-3 text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-200 font-medium transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Discount Modal */}
          {isDiscountModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-800">
                    Add Discount
                  </h3>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Discount Input Section */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-700 font-medium">
                        Discount Amount (₹)
                      </span>
                      <span className="text-gray-500 text-sm">
                        Less Discount
                      </span>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex-1 relative">
                        <input
                          type="number"
                          max={initialGrossTotal}
                          value={masterDiscountAmountTemp}
                          onChange={(e: unknown) => {
                            if (
                              masterDiscountAmountTemp?.length === 1 &&
                              masterDiscountAmountTemp === 0
                            ) {
                              setMasterDiscountAmountTemp("");
                            }
                            setMasterDiscountAmountTemp(Number(e.target.value));
                          }}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 font-medium">₹</span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Section */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Gross Total</span>
                        <span className="font-medium">
                          ₹ {initialGrossTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Discount Amount</span>
                        <span className="font-medium text-red-600">
                          ₹{" "}
                          {(parseFloat(masterDiscountAmountTemp) || 0).toFixed(
                            2
                          )}
                        </span>
                      </div>
                      <div className="border-t border-gray-200 pt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-800 font-semibold text-lg">
                            NET AMOUNT
                          </span>
                          <span className="text-green-600 font-bold text-xl">
                            ₹{" "}
                            {(
                              initialGrossTotal -
                              (parseFloat(masterDiscountAmountTemp) || 0)
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setIsDiscountModalOpen(false);
                        setMasterDiscountAmountTemp("");
                      }}
                      className="flex-1 px-6 py-3 text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-200 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={applyDiscountToAllProducts}
                      className="flex-1 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 font-medium transition-colors"
                    >
                      Apply Discount
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InwardNewstockModal;
